import { derived, readable } from 'svelte/store';

export interface ViewportState {
  width: number;
  isMobile: boolean; // < 768px
  isTablet: boolean; // 768–1023px
  isDesktop: boolean; // >= 1024px
}

const MOBILE_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1024;

function getState(width: number): ViewportState {
  return {
    width,
    isMobile: width < MOBILE_BREAKPOINT,
    isTablet: width >= MOBILE_BREAKPOINT && width < DESKTOP_BREAKPOINT,
    isDesktop: width >= DESKTOP_BREAKPOINT,
  };
}

/**
 * Shared reactive viewport store. Replaces per-component window.innerWidth
 * state + resize listeners (previously in Projects/Index, Projects/Show, etc.).
 *
 * Usage:
 *   import { viewport } from '../lib/stores/viewport';
 *   $viewport.isMobile  // true when < 768px
 */
export const viewport = readable<ViewportState>(
  getState(typeof window !== 'undefined' ? window.innerWidth : 1440),
  (set) => {
    if (typeof window === 'undefined') return;

    let timer: ReturnType<typeof setTimeout>;
    function update() {
      set(getState(window.innerWidth));
    }

    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(update, 100);
    };

    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  },
);

/** Touch-first devices: no hover and a coarse (finger) pointer. */
export const COARSE_POINTER_QUERY = '(hover: none) and (pointer: coarse)';

/**
 * True on touch-first devices. Use for behaviour differences (autofocus,
 * tooltips, keyboard hints), not layout — layout uses `$viewport.isMobile`.
 * Safe to call during SSR / in environments without matchMedia (returns false).
 */
export function isCoarsePointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(COARSE_POINTER_QUERY).matches;
}

/** Reactive version of `isCoarsePointer()` (e.g. `$coarsePointer`). */
export const coarsePointer = readable<boolean>(isCoarsePointer(), (set) => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

  const mql = window.matchMedia(COARSE_POINTER_QUERY);
  set(mql.matches);
  const onChange = (e: MediaQueryListEvent) => set(e.matches);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
});

/**
 * True where drag and drop needs a tap alternative (plans/mobile-v2.md §3.5):
 * phone widths, where `draggable` is off, and coarse pointers, where the touch
 * layer hides `.drag-handle` grips. Gate "Move up / Move down" and similar menu
 * items on it (e.g. `$tapAlternatives`).
 */
export const tapAlternatives = derived(
  [viewport, coarsePointer],
  ([$viewport, $coarsePointer]) => $viewport.isMobile || $coarsePointer,
);
